import { Interface } from '@ethersproject/abi'
import { BigNumber } from '@ethersproject/bignumber'
import {
  ERC20_ABI,
  READER_ABI,
  READER_ADDRESS,
  PAIR_ABI,
  ORACLE_ABI,
  UNISWAP_V2_PAIR_ABI,
  toToken,
  ChainId,
  UNISWAP_V3_POOL_ABI,
  UNISWAP_V3_BASED_PAIRS,
} from '../constants'
import { Currency, getPairAddress, Pair, PairState } from '../model'
import { bigNumberFromHex, BN_10_18, sortTokens } from '../utils'
import { useChainCall } from './useChainCalls'
import { useEthers } from './useEthers'

const BN_2_112 = BigNumber.from(2).pow(112)
const BN_2_192 = BigNumber.from(2).pow(192) // (2^96)^2

export function usePair(first: Currency, second: Currency) {
  const pairAddress = getPairAddress(first, second)
  const parameters = usePairParameters(pairAddress)
  const totalSupply = useTotalSupply(pairAddress)

  // Uniswap spot price
  const oracleAddress = useOracleAddress(pairAddress)
  const uniswapPairAddress = useUniswapPairAddress(oracleAddress)
  const uniswapV2Reserves = useUniswapV2Reserves(pairAddress, uniswapPairAddress)
  const uniswapV3Price = useUniswapV3Price(pairAddress, uniswapPairAddress, first, second)

  // Limits
  const priceDeviationLimit = usePriceDeviationLimit(pairAddress)
  const token0AbsoluteLimit = useToken0AbsoluteLimit(pairAddress)
  const token0RelativeLimit = useToken0RelativeLimit(pairAddress)
  const token1AbsoluteLimit = useToken1AbsoluteLimit(pairAddress)
  const token1RelativeLimit = useToken1RelativeLimit(pairAddress)

  if (pairAddress) {
    let pair
    if (
      parameters &&
      totalSupply &&
      (uniswapV2Reserves || uniswapV3Price) &&
      uniswapPairAddress &&
      priceDeviationLimit &&
      token0AbsoluteLimit &&
      token0RelativeLimit &&
      token1AbsoluteLimit &&
      token1RelativeLimit
    ) {
      const state = {
        ...parameters,
        totalSupply,
        priceDeviationLimit,
        token0AbsoluteLimit,
        token0RelativeLimit,
        token1AbsoluteLimit,
        token1RelativeLimit,
      }

      // Set price equal to Uniswap spot price
      if (uniswapV3Price) {
        state.price = uniswapV3Price
      } else if (uniswapV2Reserves) {
        state.price = getUniswapV2Price(uniswapV2Reserves, first, second)
      }

      // Force curve to recenter
      state.reference0 = state.reserve0
      state.reference1 = state.reserve1

      pair = new Pair(pairAddress, first, second, state)
    }
    return {
      pair,
      exists: true,
    }
  }
  return { exists: false }
}

function getUniswapV2Price(
  uniswapV2Reserves: { reserve0: BigNumber; reserve1: BigNumber },
  first: Currency,
  second: Currency
) {
  const { reserve0, reserve1 } = uniswapV2Reserves
  const { multiplier, divider } = getPriceMultipliers(first, second)
  return reserve1.mul(BN_2_112).div(reserve0).mul(BN_10_18).mul(multiplier).div(divider).div(BN_2_112)
}

function isPairUniswapV3Based(pair: string, chainId: ChainId) {
  return UNISWAP_V3_BASED_PAIRS.map((pairAddresses) => pairAddresses[chainId]).some(
    (pairV3Based) => pairV3Based.toLowerCase() === pair.toLowerCase()
  )
}

const coder = new Interface([...READER_ABI, ...ERC20_ABI, ...PAIR_ABI, ...ORACLE_ABI])
const uniswapV2Coder = new Interface(UNISWAP_V2_PAIR_ABI)
const uniswapV3Coder = new Interface(UNISWAP_V3_POOL_ABI)

function useUniswapV3Price(
  pairAddress: string | undefined,
  uniswapV3PoolAddress: string | undefined,
  first: Currency,
  second: Currency
) {
  const { chainId = ChainId.Mainnet } = useEthers()
  const result = useChainCall(
    !!pairAddress &&
      !!uniswapV3PoolAddress &&
      isPairUniswapV3Based(pairAddress, chainId) && {
        address: uniswapV3PoolAddress,
        data: uniswapV3Coder.encodeFunctionData('slot0'),
      }
  )
  if (result) {
    const { multiplier, divider } = getPriceMultipliers(first, second)
    const decoded = uniswapV3Coder.decodeFunctionResult('slot0', result)
    const price = decoded.sqrtPriceX96.pow(2).mul(BN_10_18).mul(multiplier).div(divider).div(BN_2_192)
    return price
  }
}

function getPriceMultipliers(first: Currency, second: Currency) {
  const [token0, token1] = sortTokens(toToken(first), toToken(second))
  const multiplier = token0.decimals > token1.decimals ? 10 ** (token0.decimals - token1.decimals) : 1
  const divider = token1.decimals > token0.decimals ? 10 ** (token1.decimals - token0.decimals) : 1
  return { multiplier, divider }
}

function usePairParameters(pairAddress: string | undefined): Omit<PairState, 'totalSupply'> | undefined {
  const { chainId = ChainId.Mainnet } = useEthers()
  const result = useChainCall(
    !!pairAddress && {
      address: READER_ADDRESS[chainId],
      data: coder.encodeFunctionData('getPairParameters', [pairAddress]),
    }
  )
  if (result) {
    return coder.decodeFunctionResult('getPairParameters', result) as any
  }
}

function useTotalSupply(pairAddress: string | undefined) {
  const result = useChainCall(
    !!pairAddress && {
      address: pairAddress,
      data: coder.encodeFunctionData('totalSupply'),
    }
  )
  if (result) {
    return bigNumberFromHex(result)
  }
}

function useOracleAddress(pairAddress: string | undefined) {
  const result = useChainCall(
    !!pairAddress && {
      address: pairAddress,
      data: coder.encodeFunctionData('oracle'),
    }
  )
  if (result) {
    return coder.decodeFunctionResult('oracle', result)[0] as string
  }
}

function useUniswapPairAddress(oracleAddress: string | undefined) {
  const result = useChainCall(
    !!oracleAddress && {
      address: oracleAddress,
      data: coder.encodeFunctionData('uniswapPair'),
    }
  )
  if (result) {
    return coder.decodeFunctionResult('uniswapPair', result)[0] as string
  }
}

function useUniswapV2Reserves(pairAddress: string | undefined, uniswapPairAddress: string | undefined) {
  const { chainId = ChainId.Mainnet } = useEthers()
  const result = useChainCall(
    !!uniswapPairAddress &&
      !!pairAddress &&
      !isPairUniswapV3Based(pairAddress, chainId) && {
        address: uniswapPairAddress,
        data: uniswapV2Coder.encodeFunctionData('getReserves'),
      }
  )
  if (result) {
    const [reserve0, reserve1] = uniswapV2Coder.decodeFunctionResult('getReserves', result) as BigNumber[]
    return {
      reserve0,
      reserve1,
    }
  }
}

function usePriceDeviationLimit(pairAddress: string | undefined) {
  const result = useChainCall(
    !!pairAddress && {
      address: pairAddress,
      data: coder.encodeFunctionData('priceDeviationLimit'),
    }
  )
  if (result) {
    return result !== '0x' ? BigNumber.from(result) : BigNumber.from(0)
  }
}

function useToken0AbsoluteLimit(pairAddress: string | undefined) {
  const result = useChainCall(
    !!pairAddress && {
      address: pairAddress,
      data: coder.encodeFunctionData('token0AbsoluteLimit'),
    }
  )
  if (result) {
    return result !== '0x' ? BigNumber.from(result) : BigNumber.from(0)
  }
}

function useToken0RelativeLimit(pairAddress: string | undefined) {
  const result = useChainCall(
    !!pairAddress && {
      address: pairAddress,
      data: coder.encodeFunctionData('token0RelativeLimit'),
    }
  )
  if (result) {
    return result !== '0x' ? BigNumber.from(result) : BigNumber.from(0)
  }
}

function useToken1AbsoluteLimit(pairAddress: string | undefined) {
  const result = useChainCall(
    !!pairAddress && {
      address: pairAddress,
      data: coder.encodeFunctionData('token1AbsoluteLimit'),
    }
  )
  if (result) {
    return result !== '0x' ? BigNumber.from(result) : BigNumber.from(0)
  }
}

function useToken1RelativeLimit(pairAddress: string | undefined) {
  const result = useChainCall(
    !!pairAddress && {
      address: pairAddress,
      data: coder.encodeFunctionData('token1RelativeLimit'),
    }
  )
  if (result) {
    return result !== '0x' ? BigNumber.from(result) : BigNumber.from(0)
  }
}
